import $api from '../http';

class MarkingService {
    // MoySklad API методы
    static async sendMarkingCodesToMoySklad(demandId, positionId, markingCodes) {
        const response = await $api.post('/marking/send-to-moysklad', {
            demandId,
            positionId,
            markingCodes
        });
        return response.data;
    }

    static async checkMarkingCode(code) {
        const response = await $api.post('/marking/check-code', {
            code
        });
        return response.data;
    }

    // ChestnyZnak API методы

    static async checkChestnyZnakMultiple(codes) {
        try {
            const response = await $api.post('/chestnyznak/check-multiple', {
                codes
            });
            return response.data;
        } catch (error) {
            const errorData = error.response?.data || {};

            // Возвращаем структурированный ответ об ошибке
            return {
                success: false,
                totalRequested: codes.length,
                totalProcessed: 0,
                error: errorData.error || error.message,
                errorCode: errorData.errorCode,
                errorType: errorData.errorType,
                isRetryable: errorData.isRetryable,
                requiresReauth: errorData.requiresReauth,
                timestamp: errorData.timestamp,
                details: errorData.details
            };
        }
    }

    static async checkChestnyZnakCode(code) {
        try {
            const response = await $api.post('/chestnyznak/validate', {
                code
            });
            return response.data;
        } catch (error) {
            const errorData = error.response?.data || {};

            // Если это наш новый формат ответа с детальной информацией (даже с ошибкой 404)
            // или это структура ошибки с полем message/errorCode, обрабатываем как результат проверки
            if ((errorData.success !== undefined && errorData.cisInfo) ||
                (errorData.success === false && (errorData.message || errorData.errorCode || errorData.cisInfo))) {
                return {
                    code: errorData.code || code,
                    isValid: errorData.isValid || false,
                    message: errorData.message || errorData.error || 'Код не найден в системе',
                    errorType: errorData.errorType,
                    isRetryable: errorData.isRetryable,
                    requiresReauth: errorData.requiresReauth,
                    errorCode: errorData.errorCode,
                    details: errorData.details,
                    cisError: errorData.cisError,
                    timestamp: errorData.timestamp,
                    cisInfo: errorData.cisInfo
                };
            }

            // Фолбэк для других форматов ошибок
            return {
                code: code,
                isValid: false,
                message: errorData.error || errorData.message || error.message || 'Произошла ошибка при проверке кода',
                errorType: errorData.errorType || 'unknown',
                isRetryable: errorData.isRetryable || false,
                requiresReauth: errorData.requiresReauth || false,
                errorCode: errorData.errorCode,
                details: errorData.details
            };
        }
    }

    static async getChestnyZnakStatus() {
        const response = await $api.get('/chestnyznak/status');
        return response.data;
    }

    static async clearChestnyZnakCache() {
        const response = await $api.post('/chestnyznak/clear-cache');
        return response.data;
    }

    // Удобный метод для быстрой проверки кода
    static async quickCodeCheck(code) {
        try {
            // Сначала проверяем через локальный кэш Redis
            const localCheck = await this.checkMarkingCode(code);
            if (localCheck.isUsed) {
                return {
                    source: 'local',
                    valid: false,
                    used: true,
                    message: 'Код уже используется в системе'
                };
            }

            // Затем проверяем через Честный ЗНАК
            const chestnyCheck = await this.checkChestnyZnakCode(code);

            // Если код невалидный (не найден, ошибка и т.д.) - передаем как ошибку
            if (!chestnyCheck.isValid) {
                return {
                    source: 'error',
                    valid: false,
                    used: false,
                    message: chestnyCheck.message,
                    errorCode: chestnyCheck.errorCode,
                    errorType: chestnyCheck.errorType,
                    isRetryable: chestnyCheck.isRetryable,
                    requiresReauth: chestnyCheck.requiresReauth,
                    timestamp: chestnyCheck.timestamp,
                    details: chestnyCheck.details,
                    code: chestnyCheck.code,
                    cisError: chestnyCheck.cisError,
                    cisInfo: chestnyCheck.cisInfo
                };
            }

            // Код валидный - передаем как результат проверки
            return {
                source: 'chestnyznak',
                valid: chestnyCheck.isValid,
                used: false,
                message: chestnyCheck.message,
                details: chestnyCheck,
                errorCode: chestnyCheck.errorCode,
                errorType: chestnyCheck.errorType,
                timestamp: chestnyCheck.timestamp,
                cisInfo: chestnyCheck.cisInfo,
                // Добавляем информацию о статусах из нового API
                isAvailableForSale: chestnyCheck.isAvailableForSale,
                isOwnedByFLX: chestnyCheck.isOwnedByFLX,
                isSold: chestnyCheck.isSold,
                // Добавляем дополнительные детальные данные для валидных кодов
                ...(chestnyCheck.details && chestnyCheck.details.cisInfo ? { cisInfo: chestnyCheck.details.cisInfo } : {})
            };

        } catch (error) {
            // Обрабатываем новую структуру ошибок от бэкенда
            const errorData = error.response?.data || {};

            // Если это уже наша структура ошибок
            if (errorData.success === false && errorData.error) {
                return {
                    source: 'error',
                    valid: false,
                    used: false,
                    message: errorData.error,
                    errorCode: errorData.errorCode,
                    errorType: errorData.errorType,
                    isRetryable: errorData.isRetryable,
                    requiresReauth: errorData.requiresReauth,
                    timestamp: errorData.timestamp,
                    details: errorData.details,
                    code: errorData.code,
                    cisError: errorData.cisError,
                    // Добавляем информацию о статусах даже для ошибок
                    isAvailableForSale: errorData.isAvailableForSale,
                    isOwnedByFLX: errorData.isOwnedByFLX,
                    isSold: errorData.isSold,
                    cisInfo: errorData.cisInfo
                };
            }

            // Фолбэк для старых форматов ошибок
            return {
                source: 'error',
                valid: false,
                used: false,
                message: error.response?.data?.error || error.message || 'Произошла ошибка при проверке кода'
            };
        }
    }
}

export default MarkingService;